import {mergeAttributes, Node} from "@tiptap/core";
import {ReactNodeViewRenderer} from "@tiptap/react";
import PrintoutDividerNodeView from "./PrintoutDividerNodeView";
import {
    clampDividerThickness,
    DEFAULT_DIVIDER_THICKNESS,
    parseDividerLabel,
} from "./printoutDividerOptions";

export interface PrintoutDividerNodeAttrs {
    color?: string | null;
    label?: string | null;
    role?: "footer" | null;
    thickness?: number;
    variant?: "solid";
}

export const PrintoutDividerNode = Node.create({
    name: "printoutDivider",

    group: "block",
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            variant: {
                default: "solid",
                parseHTML: element => element.getAttribute("data-printout-divider-variant") || "solid",
                renderHTML: attributes => ({
                    "data-printout-divider-variant": attributes.variant || "solid",
                }),
            },
            color: {
                default: null,
                parseHTML: element => element.getAttribute("data-printout-divider-color"),
                renderHTML: attributes => attributes.color
                    ? {"data-printout-divider-color": attributes.color}
                    : {},
            },
            role: {
                default: null,
                parseHTML: element => element.getAttribute("data-printout-divider-role") === "footer"
                    ? "footer"
                    : null,
                renderHTML: attributes => attributes.role === "footer"
                    ? {"data-printout-divider-role": "footer"}
                    : {},
            },
            thickness: {
                default: DEFAULT_DIVIDER_THICKNESS,
                parseHTML: element => clampDividerThickness(element.getAttribute("data-printout-divider-thickness")),
                renderHTML: attributes => ({
                    "data-printout-divider-thickness": clampDividerThickness(attributes.thickness),
                }),
            },
            label: {
                default: null,
                parseHTML: element => parseDividerLabel(element.getAttribute("data-printout-divider-label")),
                renderHTML: attributes => attributes.label
                    ? {"data-printout-divider-label": attributes.label}
                    : {},
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="printout-divider"]',
            },
        ];
    },

    renderHTML({HTMLAttributes, node}) {
        const color = typeof node.attrs.color === "string" ? node.attrs.color : null;
        const label = parseDividerLabel(node.attrs.label);
        const thickness = clampDividerThickness(node.attrs.thickness);
        const lineStyle = [
            "border:0",
            `height:${thickness}px`,
            "width:100%",
            color ? `background-color:${color}` : "",
        ].filter(Boolean).join(";");
        const line = ["div", {class: "printout-rendered-divider-line", style: lineStyle}];

        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-divider",
                class: label
                    ? "printout-rendered-divider-block printout-rendered-divider-block-labeled"
                    : "printout-rendered-divider-block",
            }),
            ...(label
                ? [
                    line,
                    ["span", {class: "printout-rendered-divider-label", ...(color ? {style: `color:${color}`} : {})}, label],
                    line,
                ]
                : [line]),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutDividerNodeView, {
            trackNodeViewPosition: true,
        });
    },
});
